<template>
	<div class="view-invoice p-4">
		<Modal
			:show.sync="showInvoice"
			content_class="w-full lg:w-10/12 xl:w-9/12 confirm-modal"
		>
			<template #content v-if="selectInvoiceDetails">
				<div
					v-for="invoice_details in selectInvoiceDetails"
					:key="invoice_details.invoiceSystemRef"
				>
					<div class="flex items-start justify-between mb-4">
						<div class="">
							<div
								class="text-lg font-semibold text-text-primary"
							>
								Invoice Details
							</div>
						</div>
						<button
							class="text-gray-400 hover:text-gray-600"
							@click="$store.commit('showInvoice', false)"
							aria-label="Close"
						>
							<i class="fa fa-times"></i>
						</button>
					</div>

					<div class="">
						<div class="flex justify-center">
							<div class="w-full lg:w-11/12 xl:w-10/12">
								<div
									class="relative bg-white rounded-lg border border-border shadow-md overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand-navy"
								>
									<!-- Invoice Header Section (Metadata) -->
									<div
										class="bg-surface border-b border-border p-6"
									>
										<div
											class="flex flex-col lg:flex-row lg:justify-between gap-4"
										>
											<div class="flex flex-col gap-4">
												<div
													class="flex items-center gap-3"
												>
													<div
														class="text-sm font-semibold text-text-secondary w-24"
													>
														From:
													</div>
													<div
														class="text-sm font-bold text-text-primary"
													>
														{{
															invoice_details.from
																.entityType
														}}
													</div>
												</div>
												<div
													class="flex items-center gap-3"
												>
													<div
														class="text-sm font-semibold text-text-secondary w-24"
													>
														Invoice Ref:
													</div>
													<div
														class="text-sm font-bold text-text-primary"
													>
														{{
															invoice_details.invoiceSystemRef
														}}
													</div>
												</div>
											</div>

											<div
												class="flex flex-col gap-4 items-start lg:items-end"
											>
												<div
													class="flex items-center gap-3"
												>
													<div
														class="text-sm font-semibold text-text-secondary w-24"
													>
														Date:
													</div>
													<div
														class="text-sm font-bold text-text-primary"
													>
														{{
															moment(
																invoice_details.createdDate,
															).format('DD/MM/YY')
														}}
													</div>
												</div>
												<div
													class="flex items-center gap-3"
												>
													<div
														class="text-sm font-semibold text-text-secondary w-24"
													>
														Status:
													</div>
													<div
														class="text-xs font-semibold px-3 py-1 rounded-full"
														:class="
															invoice_details.invoiceStatus ===
															'PENDING-PAYMENT'
																? 'text-yellow-600 bg-yellow-50'
																: 'text-green-600 bg-green-50'
														"
													>
														{{
															invoice_details.invoiceStatus ===
															'PENDING-PAYMENT'
																? 'Pending'
																: 'Paid'
														}}
													</div>
												</div>
											</div>
										</div>
									</div>

									<!-- Invoice Reference Section -->
									<div
										class="p-5 bg-white border-b border-border"
									>
										<div class="flex items-center gap-3">
											<span
												class="text-sm font-semibold text-text-secondary"
												>Ref:</span
											>
											<span
												class="text-sm font-bold text-primary"
												>{{
													invoice_details.invoiceCategory ||
													'Quotation Invoice'
												}}</span
											>
										</div>
									</div>

									<!-- Itemized Table -->
									<div v-if="invoice_details.items" class="">
										<div
											class="grid grid-cols-12 gap-4 bg-background border-b border-border p-4 text-xs font-semibold uppercase tracking-wide text-text-primary"
										>
											<div
												class="col-span-6 lg:col-span-7"
											>
												Description
											</div>
											<div class="col-span-2 text-center">
												Qty
											</div>
											<div
												class="col-span-2 lg:col-span-2 text-center"
											>
												Value
											</div>
											<div
												class="hidden lg:block col-span-2 text-center"
											>
												Amount
											</div>
										</div>

										<div
											v-for="(
												item, k
											) in invoice_details.items"
											:key="k"
											class="grid grid-cols-12 gap-4 border-b border-border p-4 items-center hover:bg-white"
										>
											<div
												class="col-span-6 lg:col-span-7 text-sm text-text-primary"
											>
												{{ item.description }}
											</div>
											<div
												class="col-span-2 text-center text-sm font-semibold text-text-primary"
											>
												{{ item.quantity }}
											</div>
											<div
												class="col-span-2 lg:col-span-2 text-center text-sm font-semibold text-text-primary"
											>
												{{
													utils.numberWithCommas2(
														item.value || 0,
													)
												}}
											</div>
											<div
												class="hidden lg:block col-span-2 text-center text-sm font-semibold text-brand-cyan"
											>
												{{
													utils.numberWithCommas2(
														item.amount || 0,
													)
												}}
											</div>
										</div>
									</div>

									<!-- Taxes/Charges Section -->
									<div
										class="p-6 bg-surface border-b border-border"
									>
										<div class="mb-3">
											<h4
												class="text-sm font-bold text-text-primary m-0"
											>
												Taxes / Charges
											</h4>
										</div>
										<div class="space-y-2">
											<div
												v-for="tax in invoice_details.taxes"
												:key="tax.taxName"
												class="flex justify-between items-center py-1 border-b border-border last:border-b-0"
											>
												<span
													class="text-sm text-text-secondary"
													>{{ tax.taxName }}</span
												>
												<span
													class="text-sm font-semibold text-text-primary"
													>{{
														utils.numberWithCommas2(
															tax.taxAmount,
														)
													}}</span
												>
											</div>
										</div>
									</div>

									<!-- Final Total Section -->
									<div class="p-6 bg-white">
										<div
											class="flex justify-between items-center py-1"
										>
											<div
												class="text-sm font-semibold text-text-primary"
											>
												Subtotal:
											</div>
											<div
												class="text-sm font-semibold text-text-primary"
											>
												{{
													invoice_details.invoiceCurrency
												}}
												{{
													getSubtotal(invoice_details)
												}}
											</div>
										</div>
										<div
											class="flex justify-between items-center py-1"
										>
											<div
												class="text-sm font-semibold text-text-primary"
											>
												Taxes:
											</div>
											<div
												class="text-sm font-semibold text-text-primary"
											>
												{{
													invoice_details.invoiceCurrency
												}}
												{{
													getTotalTaxAmount(
														invoice_details,
													)
												}}
											</div>
										</div>
										<div
											class="flex justify-between items-center pt-4 mt-4 border-t border-border"
										>
											<div
												class="text-base font-bold text-text-primary"
											>
												Grand Total:
											</div>
											<div
												class="text-lg font-bold text-primary"
											>
												{{
													invoice_details.invoiceAmount.toFixed(
														2,
													)
												}}
											</div>
										</div>
									</div>

									<!-- Accent Bar -->
									<div
										class="h-1 w-16 transition-all"
										:class="
											invoice_details.invoiceStatus ===
											'PAID'
												? 'bg-brand-cyan'
												: 'bg-accent'
										"
									></div>
								</div>

								<!-- Action Buttons -->
								<div class="mt-6">
									<div
										class="flex flex-col md:flex-row md:justify-center md:items-center gap-3"
									>
										<div class="md:w-1/3">
											<button
												@click="downloadInvoice()"
												class="w-full inline-flex items-center justify-center gap-3 px-4 py-3 rounded-lg bg-primary text-white font-semibold shadow hover:opacity-95"
											>
												<i class="fas fa-download"></i>
												<span>Download</span>
											</button>
										</div>
										<div
											class="md:w-1/3"
											v-if="
												invoice_details.invoiceStatus !==
												'PAID'
											"
										>
											<button
												@click="
													$store.commit(
														'showMakePayment',
														true,
													)
												"
												class="w-full inline-flex items-center justify-center gap-3 px-4 py-3 rounded-lg bg-brand-cyan text-white font-semibold shadow hover:opacity-95"
											>
												<i
													class="fas fa-credit-card"
												></i>
												<span>Make Payment</span>
											</button>
										</div>
										<div class="w-full" v-else>
											<div
												class="w-full inline-flex items-center justify-center gap-3 px-4 py-3 rounded-lg bg-green-50 text-green-600 font-semibold"
											>
												<i
													class="fas fa-check-circle"
												></i>
												<span
													>Invoice Already Paid</span
												>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
	components: {
		Modal: () => import('../templates/others/modal'),
	},
	computed: {
		...mapGetters([
			'loading',
			'selected_invoice',
			'invoice_details',
			'account_details',
		]),
		...mapGetters('invoices', ['selectInvoiceDetails']),

		// showInvoice: sync modal visibility with Vuex (getter + setter)
		showInvoice: {
			get() {
				return this.$store.getters.showInvoice;
			},
			set(val) {
				this.$store.commit('showInvoice', val);
			},
		},
	},
	data() {
		return {
			api,
			utils,
			moment,
		};
	},
	methods: {
		...mapActions(['clickAction', 'showMakePayment']),

		downloadInvoice() {
			console.log('Download invoice functionality');
		},

		getTotalTaxAmount(invoice) {
			if (!invoice || !invoice.taxes || !Array.isArray(invoice.taxes))
				return '0.00';
			const total = invoice.taxes.reduce(
				(sum, tax) => sum + (parseFloat(tax.taxAmount) || 0),
				0,
			);
			return total.toFixed(2);
		},

		getSubtotal(invoice) {
			if (!invoice) return '0.00';
			const totalTax = parseFloat(this.getTotalTaxAmount(invoice)) || 0;
			const amount = parseFloat(invoice.invoiceAmount) || 0;
			return (amount - totalTax).toFixed(2);
		},
		async makeInvoicePayment() {},
	},
	mounted() {
		if (this.selectInvoiceDetails && this.selectInvoiceDetails.length) {
			console.log(
				'subTotal',
				this.getSubtotal(this.selectInvoiceDetails[0]),
			);
		}
	},
};
</script>
<style lang="less">
.invoice-box {
	font-weight: 600;
	font-size: 16px;

	i {
		font-size: 18px;
		transition: all 0.3s ease;
	}

	&:hover {
		i {
			transform: scale(1.1);
		}
	}

	@media (max-width: 768px) {
		.invoice-header-section {
			.header-row {
				flex-direction: column;
				gap: 16px;
			}
		}

		.itemized-table {
			.table-header,
			.table-row {
				grid-template-columns: 1fr 60px 80px 80px;
				gap: 8px;
				padding: 12px 16px;
			}
		}

		.action-section {
			.row {
				flex-direction: column;
				gap: 12px;

				.col-md-5 {
					width: 100%;
					margin: 0 !important;
				}
			}
		}
	}

	&:focus {
		outline: 2px solid #1db584;
		outline-offset: 2px;
	}

	&.error {
		border-color: #f44336;
		background: #fff5f5;

		.accent-bar {
			background: #f44336;
		}
	}
}
</style>
